/** @jsxImportSource @emotion/react */
import React, { useCallback } from "react";

import ColorSchema from "../kits/Color";
import { ForbiddenIcon, ListIcon, PlusIcon } from "../kits/Icons";
import { css } from "@emotion/react";
import { CustomTooltip } from "../kits/tooltip/CustomTooltip";

/**
 * Using button tag instead of CustomButton component because CustomButton component has background color and border and some other color fields that we don't want.
 */

type CardInfoType = {
  value: string | number;
  label: string;
}[];

const LabelValueStyle = css({
  width: "100%",
  padding: "10px 20px",
  textAlign: "center",
  "@media (max-width: 800px)": {
    fontSize: "0.7rem",
    padding: "5px 10px",
  },
});

interface MakeItemProps {
  label: string;
  value: JSX.Element | string;
}

const MakeItem: React.FC<MakeItemProps> = ({ label, value }) => (
  <div
    css={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "start",
      borderLeft: `2px solid ${ColorSchema.white_custom}`,
      flexGrow: 1,
    }}
  >
    <h6
      style={{
        fontWeight: "bold",
        borderStyle: "solid",
        borderWidth: "3px 0 1px",
        borderColor: ColorSchema.white_custom,
        height: 60,
      }}
      css={LabelValueStyle}
    >
      {label}
    </h6>
    <p
      style={{
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      css={LabelValueStyle}
    >
      {value}
    </p>
  </div>
);

interface TransactionCardProps {
  id: number;
  isSuspension?: boolean;
  info: CardInfoType;
  suspensionCallback: Function;
}

const TransactionCard: React.FC<TransactionCardProps> = ({
  info,
  id,
  isSuspension = false,
  suspensionCallback,
}) => {
  return (
    <div
      css={{
        display: "flex",
        flexWrap: "wrap",
        backgroundColor: isSuspension
          ? ColorSchema.danger_custom
          : ColorSchema.primary_custom,
        color: ColorSchema.white_custom,
        overflow: "hidden",
        marginBottom: 20,
      }}
    >
      {info.map(({ value, label }, index) => (
        <MakeItem
          key={`transaction-card-${id}-field-${index}`}
          label={label}
          value={"" + value}
        />
      ))}
      {isSuspension || (
        <MakeItem
          key={`transaction-card-${id}-field-${info.length}`}
          label={"عملیات‌ها"}
          value={
            <CustomTooltip
              title="تعلیق تراکنش"
              color="dark_custom"
              placement="top"
              className=""
              style={{}}
            >
              <button
                className="btn btn-link p-0"
                css={{
                  textDecoration: "none",
                  color: "inherit",
                  borderRadius: "50%",
                }}
                onClick={() => suspensionCallback()}
              >
                <ForbiddenIcon color={ColorSchema.white_custom} />
              </button>
            </CustomTooltip>
          }
        />
      )}
    </div>
  );
};

interface CustomerCardProps {
  id: number;
  info: CardInfoType;
  getReports: Function;
  addTransaction: Function;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  info,
  id,
  getReports,
  addTransaction,
}) => {
  return (
    <div
      css={{
        display: "flex",
        flexWrap: "wrap",
        backgroundColor: ColorSchema.primary_custom,
        color: ColorSchema.white_custom,
        overflow: "hidden",
        marginBottom: 20,
      }}
    >
      {info.map(({ value, label }, index) => (
        <MakeItem
          key={`customer-card-${id}-field-${index}`}
          label={label}
          value={"" + value}
        />
      ))}
      <MakeItem
        key={`customer-card-${id}-field-${info.length}`}
        label={"عملیات‌ها"}
        value={
          <>
            <CustomTooltip
              title="گزارش"
              color="dark_custom"
              placement="top"
              className=""
              style={{}}
            >
              <button
                className="btn btn-link p-0"
                css={{
                  textDecoration: "none",
                  color: "inherit",
                  borderRadius: "50%",
                }}
                onClick={() => getReports()}
              >
                <ListIcon color={ColorSchema.white_custom} />
              </button>
            </CustomTooltip>
            <CustomTooltip
              title="افزودن تراکنش"
              color="dark_custom"
              placement="top"
              className=""
              style={{}}
            >
              <button
                className="btn btn-link p-0"
                css={{
                  textDecoration: "none",
                  color: "inherit",
                  borderRadius: "50%",
                }}
                onClick={() => addTransaction()}
              >
                <PlusIcon color={ColorSchema.white_custom} />
              </button>
            </CustomTooltip>
          </>
        }
      />
    </div>
  );
};

export { TransactionCard, CustomerCard };

export type { CardInfoType };
